import { Feld, FeldHuelle, type FeldEigenschaften, type FeldGroesse } from "./feld";

/**
 * Natives Auswahlfeld (F6: Standard für kurze Listen; ab etwa 8 Optionen `AuswahlErweitert`).
 * Optionen als Daten (`optionen`, `gruppen`) oder – für gruppierte Personenlisten des Aufrufers – als
 * `<option>`/`<optgroup>`-Kinder. Die Liste füllt der Aufrufer nur mit dem, was die Action erlaubt (r110).
 */
export interface AuswahlOption {
  wert: string;
  label: string;
  deaktiviert?: boolean;
}
export interface AuswahlGruppe {
  label: string;
  optionen: AuswahlOption[];
}

type AuswahlRest = Omit<React.SelectHTMLAttributes<HTMLSelectElement>, "id" | "className" | "style" | "children" | "size" | "multiple">;

const KLASSEN: Record<FeldGroesse, { normal: string; fehler: string }> = {
  standard: {
    normal:
      "py-3 px-4 pe-9 block w-full bg-layer border-layer-line rounded-lg text-sm text-foreground focus:border-primary-focus focus:ring-primary-focus disabled:opacity-50 disabled:pointer-events-none",
    fehler:
      "py-3 ps-4 pe-16 block w-full bg-layer border-red-500 rounded-lg text-sm text-foreground focus:border-red-500 focus:ring-red-500 disabled:opacity-50 disabled:pointer-events-none",
  },
  kompakt: {
    normal:
      "py-2 px-3 pe-9 block w-full bg-layer border-layer-line rounded-lg text-sm text-foreground focus:border-primary-focus focus:ring-primary-focus disabled:opacity-50 disabled:pointer-events-none",
    fehler:
      "py-2 ps-3 pe-16 block w-full bg-layer border-red-500 rounded-lg text-sm text-foreground focus:border-red-500 focus:ring-red-500 disabled:opacity-50 disabled:pointer-events-none",
  },
  // ruhig (A4′): Rahmen und Grund nur bei Hover und Fokus; der Fokusrahmen (Rand + Ring) ist immer sichtbar.
  ruhig: {
    normal:
      "py-1.5 px-2 pe-9 block w-full bg-transparent border-transparent rounded-md text-sm text-foreground hover:border-layer-line hover:bg-layer focus:bg-layer focus:border-primary-focus focus:ring-primary-focus disabled:opacity-50 disabled:pointer-events-none",
    fehler:
      "py-1.5 ps-2 pe-16 block w-full bg-layer border-red-500 rounded-md text-sm text-foreground focus:border-red-500 focus:ring-red-500 disabled:opacity-50 disabled:pointer-events-none",
  },
};

function Optionen({ optionen }: { optionen: AuswahlOption[] }) {
  return (
    <>
      {optionen.map((o) => (
        <option key={o.wert} value={o.wert} disabled={o.deaktiviert}>
          {o.label}
        </option>
      ))}
    </>
  );
}

export function Auswahl({
  id,
  label,
  labelVersteckt,
  optional,
  hilfe,
  fehler,
  hinweissatz,
  feldhilfe,
  layout,
  groesse = "standard",
  optionen,
  gruppen,
  platzhalter,
  children,
  ...rest
}: FeldEigenschaften &
  AuswahlRest & {
    optionen?: AuswahlOption[];
    gruppen?: AuswahlGruppe[];
    /** Erste Option ohne Wert („— bitte wählen —“, „— kein Zugewiesener —“). */
    platzhalter?: string;
    /** Eigene `<option>`/`<optgroup>`-Kinder (statt `optionen`/`gruppen`). */
    children?: React.ReactNode;
  }) {
  return (
    <Feld id={id} label={label} labelVersteckt={labelVersteckt} optional={optional} hilfe={hilfe} fehler={fehler} hinweissatz={hinweissatz} feldhilfe={feldhilfe} layout={layout} groesse={groesse}>
      {(a) => (
        <FeldHuelle>
          {/* Preline: component/select/select-label */}
          <select
            {...rest}
            id={a.id}
            className={a.fehlerhaft ? KLASSEN[groesse].fehler : KLASSEN[groesse].normal}
            aria-describedby={a["aria-describedby"]}
            aria-invalid={a["aria-invalid"]}
          >
            {platzhalter !== undefined && <option value="">{platzhalter}</option>}
            {optionen && <Optionen optionen={optionen} />}
            {gruppen?.map((g) => (
              <optgroup key={g.label} label={g.label}>
                <Optionen optionen={g.optionen} />
              </optgroup>
            ))}
            {children}
          </select>
        </FeldHuelle>
      )}
    </Feld>
  );
}
